import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * A filter that a link may set: `/admin/korisnici?search=Amra`.
 *
 * The search palette opens lists already narrowed to what was found — a
 * person on Korisnici, a machine on Radni centri, the reports filed against a
 * machine. Each page keeps its filter in its own state, so the URL is read
 * once when the page opens and again whenever it changes while the page is
 * still open — which is the case of picking a second person from the palette
 * while the first one's list is on screen. A route change remounts the page,
 * but a change of query string alone does not.
 *
 * Returns the value for a `useState` initialiser, so the first request
 * already carries the filter instead of loading everything first.
 */
export function useLinkedParam(name: string, apply: (value: string) => void): string | null {
  const [params] = useSearchParams()
  const value = params.get(name)

  // The latest setter, so a caller passing an inline function does not re-run
  // the effect on every render.
  const applyRef = useRef(apply)
  applyRef.current = apply

  const first = useRef(true)

  useEffect(() => {
    // The initialiser has already used it on the first render.
    if (first.current) {
      first.current = false
      return
    }

    if (value !== null) applyRef.current(value)
  }, [value])

  return value
}
